<template>
  <ChartHeader class="table-header" label="Active Candidates in">
    <template #appendTitle>
      <span
        v-for="({ stage, label }) in stageOptions"
        :key="stage"
        class="stage-option ml-3"
        :class="{ active: selectedStage === stage }"
        @click="$emit('set-candidate-stage', stage)"
      >{{ label }} Stage</span>
    </template>
    <template #rightSide>
      <div class="d-flex align-center">
        <InsightsTooltip :tooltip="tooltip" class="mr-3" />
        <DpplSvg
          data-png-ignore
          file-name="widget-download"
          class="widget-download mr-3"
          @click="exportInProgress ? null : downloadCSV()"
        />
        <v-icon
          size="18px"
          color="iconDefault"
          class="close-icon"
          icon="$close"
          @click="$emit('set-candidate-stage', null)"
        />
      </div>
    </template>
  </ChartHeader>
</template>

<script setup>
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { getTooltip } from '@/helpers/tooltips';
import { computed, ref } from 'vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import { useAppSnackbar } from '@/stores/app/snackbar';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';

const props = defineProps({
  selectedStage: { type: String, default: '' },
  candidateStageOptions: { type: Array, default: () => [] },
  activeCandidates: { type: Object, required: true },
  projectName: { type: String, required: true },
});

defineEmits(['set-candidate-stage']);

const { downloadActiveCandidatesCSV } = useRequisitionReport();

const exportInProgress = ref(false);

const labels = {
  screen: 'Screening',
  offer: 'Offer',
  assessment: 'Assessment',
};

const stageOptions = computed(() => Object.keys(props.activeCandidates)
  .filter((stage) => props.candidateStageOptions.includes(stage))
  .map((stage) => ({
    stage,
    label: labels[stage],
  })));

const tooltip = computed(() => getTooltip('req-report-active-candidate-table'));

const downloadCSV = async () => {
  try {
    exportInProgress.value = true;

    await downloadActiveCandidatesCSV([props.selectedStage], props.projectName);
  } catch (error) {
    useAppSnackbar().addErrorSnackbar({
      text: 'Failed to download CSV',
    });
  } finally {
    exportInProgress.value = false;
  }
};
</script>

<style scoped lang="less">
.table-header {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 16px;
}

.table-header-left {
  font-size: 16px;
  line-height: 20px;
  color: @text-darkest;
}

.table-title {
  text-align: left;
  font-weight: 400;
}

.stage-option {
  font-weight: 500;
  cursor: pointer;
  padding-bottom: 4px;
  border-bottom: 2px solid transparent;
  transform: translateY(2px);

  &:hover {
    color: @text-brand;
  }

  &.active {
    color: @text-brand;
    border-bottom: 2px solid @brand-primary;
    font-weight: 600;
    transition: all 0.5s ease;
  }
}

.widget-download {
  cursor: pointer;

  :deep(path:first-child) {
    stroke: @grey-500;
  }

  :deep(path:last-child) {
    fill: @grey-500;
  }

  &:hover {
    :deep(path:first-child) {
      stroke: @grey-900;
    }

    :deep(path:last-child) {
      fill: @icon-darkest;
    }
  }
}
</style>
